/**
 * Schwierigkeitsgrade.
 *
 * Keiner davon macht das Spiel „mehr oder weniger“ — jeder verschiebt
 * dieselben Spannungen: wie sicher die Station eine Ankunft vorhersagt, wie
 * schwer die Mannschaft an den Toten traegt, wie viel Zeit bleibt, bis der
 * Untersuchungsausschuss einen Bericht verlangt, und wie gnaedig das Sonar ist.
 *
 * Die Werte stehen hier und nirgends sonst. Die Systeme fragen nach einem
 * Faktor, nicht nach dem Namen des Grades — sonst wuchsen ueber die Zeit
 * ueberall kleine `if (hard)` heran.
 */

export const DIFFICULTIES = ['calm', 'standard', 'relentless'] as const;
export type Difficulty = (typeof DIFFICULTIES)[number];

export const DEFAULT_DIFFICULTY: Difficulty = 'standard';

export interface DifficultyRules {
  /**
   * Nach so vielen Tagen tagt der Untersuchungsausschuss, und der Bericht
   * muss vorliegen — mit dem, was man hat. `null`: keine Frist.
   */
  readonly deadlineDays: number | null;
  /** Faktor auf die Unsicherheit der Ankunftsprognose. */
  readonly forecastUncertainty: number;
  /** Faktor auf alles, was die Moral senkt. */
  readonly moraleLoss: number;
  /** Ab dieser Moral verweigert die Mannschaft Umwege fuer die Akte. */
  readonly mutinyThreshold: number;
  /** Zusaetzliche Pings bei der Suche am Fundort (auch negativ). */
  readonly sonarPings: number;
  /** Faktor auf den Abstand zwischen Notrufen. Kleiner heisst dichter. */
  readonly spawnInterval: number;
  /**
   * Faktor auf den Meldeverzug. Bei 1 kommt ein Notruf ohne Ansehen gut elf
   * Stunden zu spaet an — die Stunden fehlen genau im Rettungsfenster.
   */
  readonly reportDelay: number;
  /** Faktor auf das Rettungsfenster: wie lange Ueberlebende durchhalten. */
  readonly rescueWindow: number;
  /** Faktor auf die Rumpfbelastung bei schwerer See. */
  readonly hullStress: number;
  /** Faktor auf den Treibstoffverbrauch. */
  readonly fuelBurn: number;
}

const RULES: Readonly<Record<Difficulty, DifficultyRules>> = {
  calm: {
    deadlineDays: null,
    forecastUncertainty: 0.6,
    moraleLoss: 0.6,
    mutinyThreshold: 15,
    sonarPings: 2,
    spawnInterval: 1.2,
    reportDelay: 0.15,
    rescueWindow: 1.9,
    hullStress: 0.35,
    fuelBurn: 0.7,
  },
  standard: {
    deadlineDays: 45,
    forecastUncertainty: 1,
    moraleLoss: 1,
    mutinyThreshold: 25,
    sonarPings: 0,
    spawnInterval: 1,
    reportDelay: 0.5,
    rescueWindow: 1.4,
    hullStress: 0.6,
    fuelBurn: 0.85,
  },
  relentless: {
    deadlineDays: 30,
    forecastUncertainty: 1.4,
    moraleLoss: 1.5,
    mutinyThreshold: 35,
    sonarPings: -1,
    spawnInterval: 0.85,
    reportDelay: 1,
    rescueWindow: 1.05,
    hullStress: 1,
    fuelBurn: 1,
  },
};

export function difficultyRules(difficulty: Difficulty): DifficultyRules {
  return RULES[difficulty];
}

/** Ein gespeicherter Wert kann alles sein — nur Bekanntes wird uebernommen. */
export function parseDifficulty(value: unknown): Difficulty {
  return (DIFFICULTIES as readonly unknown[]).includes(value)
    ? (value as Difficulty)
    : DEFAULT_DIFFICULTY;
}
